---
title: "Terms of Service"
effective: "2026-07-17"
updated: "2026-08-13"
---

These Terms of Service ("**Terms**") are a binding agreement between you and
{publicEnv.siteLegalEntity} ("**Company**", "**we**", "**us**", or "**our**").
They govern your use of Makefile FYI at `makefile.fyi`, including the course and
related lessons, concepts, source snapshots, terminal replays, account features,
and billing services (together, the "**Services**").

By creating an account, purchasing access, or otherwise using the Services, you
agree to these Terms and our [Privacy Policy](/privacy). If you use the Services
for an organization, you represent that you have authority to bind that
organization. If you do not agree, do not use the Services.

## 1 Eligibility and accounts

### 1.1 Age requirements

You must be at least 13 years old to use the Services. If you have not reached
the age of majority where you live, a parent or legal guardian must review and
agree to these Terms for you. You must be an adult with legal capacity, or have
an adult act for you, to make a purchase.

The Services are not directed to children under 13, and children under 13 may
not create an account or use paid features.

### 1.2 Registration and account security

You may sign in through an email verification link or an approved third-party
identity provider. You agree to provide accurate information and keep it
current. Your account and paid access are personal to you and may not be sold,
transferred, or shared.

You are responsible for protecting login links, verification codes, sessions,
and third-party credentials used with your account. Promptly contact us if you
suspect unauthorized access. We are not responsible for losses caused by your
failure to protect your account, except where applicable law provides otherwise.

### 1.3 Account closure

You may ask us to close your account by emailing {publicEnv.siteSupportEmail}
from the address associated with it. Closing an account ends your ability to use
access attached to that account, including an active pass or lifetime access,
and does not by itself create a right to a refund. We may retain transaction and
other records where reasonably needed for tax, accounting, fraud prevention,
dispute resolution, or other legal obligations. Mandatory consumer and privacy
rights remain unaffected.

## 2 The course and services

### 2.1 Educational purpose

The Services provide educational material about GNU Make, Makefiles, shell
commands, build systems, and related software practices. They reflect an
opinionated example and are not professional, legal, security, or consulting
advice. They are not a promise that a particular technique is suitable for your
project or environment.

Software, commands, dependencies, and external systems change over time. You are
responsible for reviewing, testing, securing, and adapting any example before
using it in a development, production, or other consequential environment.

### 2.2 Course changes and future material

We may correct, revise, reorganize, add, or remove course material to keep the
Services useful and accurate. We do not promise a particular roadmap,
publication schedule, lesson count, or compatibility with any specific tool
version.

Where an offer includes future additions to this course, those additions are
included while we continue to offer Makefile FYI. The offer does not include a
separate course, product, consulting service, or other Company service unless we
expressly say so.

### 2.3 Availability

We work to keep the Services available, but maintenance, security incidents,
provider outages, legal requirements, or other events may interrupt access. We
may modify or discontinue all or part of the Services. If discontinuation
materially affects paid access, we will provide any notice or remedy required by
law and may provide an additional remedy at our discretion.

## 3 Purchases and course access

The price, duration, included material, and other offer details shown on the
pricing, billing, and hosted checkout pages at the time of purchase form part of
these Terms. If a checkout-specific term conflicts with a general description
here, the checkout-specific term controls for that purchase.

### 3.1 Checkout and payment

You must sign in before purchasing so we can attach access to your account.
Prices are displayed in U.S. dollars unless stated otherwise. You agree to pay
the price and any tax or charge displayed before you confirm the transaction.

Our payment processor handles checkout and payment details. You also agree to
the processor's terms presented during checkout. A purchase is complete only
after the processor confirms successful payment. A pending, abandoned, canceled,
reversed, or failed transaction does not grant access.

### 3.2 One-time access units and passes

New purchases use one-time access units and do not renew automatically. Unless
the offer shown at checkout says otherwise:

- One unit grants 30 consecutive days of full course access.

- Access begins when payment is successfully confirmed. If you already have an
  active pass, another unit is added after the unexpired access time rather than
  replacing it.

- Each successfully paid unit remains recorded as progress toward the lifetime
  target attached to your account, even after temporary access expires.

- When a pass expires, paid material locks again, while material identified as
  free remains available. You may later buy another unit or the remaining
  lifetime balance.

Your billing page displays the duration and number of units credited to your
account.

### 3.3 Locked lifetime target

Your first successful unit purchase locks the unit price, unit duration, and
number of paid units needed for lifetime access under that offer (your
"**lifetime target**"). Creating an account, opening a checkout, or abandoning a
checkout does not lock an offer. Later changes to the public price or target do
not change a lifetime target already locked by a successful purchase.

You may reach the target through separate pass purchases or purchase all
remaining units in one checkout. Previously credited units reduce that remaining
quantity. When the required number of units is successfully paid, lifetime
access is granted automatically.

### 3.4 Meaning of lifetime access

"**Lifetime access**" means access to the paid material in Makefile FYI for as
long as the Company continues to operate and offer this course. It refers to the
lifetime of the course, not the lifetime of any person, and does not guarantee
perpetual operation of the website or any third-party service.

Lifetime access is personal, non-transferable, and subject to these Terms. It
includes future additions to Makefile FYI while the course is offered, but not
separate courses or products. We may suspend or terminate access for a material
violation of these Terms.

### 3.5 Refunds and mandatory consumer rights

Except where required by law or expressly stated in an offer, purchases are
non-refundable after paid access is provided. Contact
{publicEnv.siteSupportEmail} if you believe a charge was unauthorized,
duplicated, incorrect, or did not provide the access described.

Nothing in these Terms limits a mandatory cooling-off, withdrawal, conformity,
refund, cancellation, or other consumer right that applies where you live. If
mandatory law conflicts with these Terms, that law controls.

### 3.6 Price changes

We may change public prices and offers for future customers or future purchases.
Changes do not alter a lifetime target already locked under Section 3.3.

## 4 Intellectual property and license

### 4.1 Course materials

The Services and their course explanations, lesson structure, exercises,
interactive experiences, visual design, and branding are owned by the Company or
its licensors and are protected by intellectual property laws.

Subject to these Terms, we grant you a limited, revocable, non-exclusive,
non-transferable license to access and use the Services for your own learning
and internal work. This license does not permit you to reproduce or distribute
substantial portions of the paid course, publish access-controlled material,
resell access, or create a competing course from our material.

You remain free to use the general knowledge and skills you learn.

### 4.2 Source snapshots and third-party material

Source snapshots may contain software and documentation under the MIT License or
another license displayed with that material. That displayed license, not the
limited course license in Section 4.1, governs your use of the covered source
files. Third-party names, links, code, and other material remain subject to
their owners' rights and applicable licenses.

### 4.3 Feedback

If you voluntarily send corrections, ideas, or other feedback, you grant us a
worldwide, perpetual, irrevocable, royalty-free right to use it without
restriction or compensation. This does not transfer ownership of unrelated
material you include, and we will handle personal information under our
[Privacy Policy](/privacy).

## 5 Acceptable use

You agree not to:

1. Break any law or infringe another person's rights while using the Services.
2. Share an account, verification code, or paid access with another person.
3. Copy, republish, sell, sublicense, or systematically extract
   access-controlled course material except as an applicable source-code license
   permits.
4. Circumvent access controls, probe for vulnerabilities without written
   permission, or gain unauthorized access to an account, system, or data.
5. Introduce malware or interfere with the security, integrity, availability, or
   normal operation of the Services.
6. Use automated requests, scraping, or similar means in a way that burdens the
   Services or evades an access restriction.
7. Misrepresent your identity or affiliation, or use the Services to deceive,
   harm, or exploit another person.

We may investigate suspected misuse and take proportionate action, including
limiting requests, suspending access, preserving relevant records, or contacting
appropriate authorities.

## 6 Third-party services and links

The Services may interact with or link to third parties for payment processing,
optional identity, source-code hosting, software documentation, and other tools
discussed in the course. Their services and content are governed by their own
terms and policies.

We do not control third-party services and are not responsible for their
availability, security, accuracy, or conduct. A link does not necessarily mean
that we endorse the third party. Your dealings with a third party are between
you and that third party.

## 7 Suspension and termination

You may stop using the Services at any time. We may suspend or terminate your
account or access if you materially or repeatedly violate these Terms, create
security or legal risk, fail to pay an amount due, or misuse the Services. When
reasonable, we will give notice and an opportunity to cure before termination,
but we may act immediately to protect users, the Company, or third parties.

Upon termination, your license to use the Services ends. Provisions that by
their nature should survive will remain in effect, including payment
obligations, intellectual property provisions, warranty disclaimers, limitations
of liability, and dispute terms.

## 8 Disclaimers

**TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE SERVICES ARE PROVIDED “AS IS” AND
“AS AVAILABLE.” THE COMPANY AND ITS LICENSORS DISCLAIM ALL EXPRESS, IMPLIED, AND
STATUTORY WARRANTIES, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
PARTICULAR PURPOSE, TITLE, NON-INFRINGEMENT, ACCURACY, AND UNINTERRUPTED OR
ERROR-FREE OPERATION.**

We do not guarantee a learning, employment, business, financial, technical, or
other result. We do not guarantee that examples are free from defects or
security risks, or that they will remain compatible with future software.

Some jurisdictions do not allow certain disclaimers, so some of the above may
not apply to you. You retain any warranty or consumer protection that cannot
lawfully be excluded.

## 9 Limitation of liability

**TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE COMPANY AND ITS OWNERS,
AFFILIATES, EMPLOYEES, CONTRACTORS, LICENSORS, AND SERVICE PROVIDERS WILL NOT BE
LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, PUNITIVE, OR CONSEQUENTIAL
DAMAGES, OR FOR LOST PROFITS, REVENUE, DATA, GOODWILL, OR BUSINESS INTERRUPTION,
ARISING FROM OR RELATING TO THE SERVICES OR THESE TERMS.**

**TO THE MAXIMUM EXTENT PERMITTED BY LAW, THEIR TOTAL AGGREGATE LIABILITY FOR
ALL CLAIMS ARISING FROM OR RELATING TO THE SERVICES OR THESE TERMS WILL NOT
EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID US FOR THE SERVICES DURING THE 12
MONTHS BEFORE THE EVENT GIVING RISE TO THE CLAIM OR (B) US$100.**

These limitations apply regardless of the legal theory and even if a remedy
fails of its essential purpose. They do not limit liability that cannot lawfully
be limited, such as liability for fraud, willful misconduct, or other matters
excluded by applicable law.

## 10 Indemnification

To the extent permitted by law, you agree to defend, indemnify, and hold
harmless the Company and its owners, affiliates, employees, contractors, and
licensors from third-party claims and reasonable costs arising from your
unlawful use of the Services, material violation of these Terms, or infringement
of another person's rights. This obligation does not apply to the extent a claim
results from the Company's own unlawful conduct, negligence, or breach of these
Terms.

## 11 Governing law and disputes

Washington law governs these Terms, without regard to conflict-of-law rules.
Before filing a claim, you and the Company agree to make a good-faith effort to
resolve it informally. Contact us at {publicEnv.siteSupportEmail} with a brief
description of the issue and the relief requested.

If informal resolution fails, the state and federal courts with jurisdiction in
King County, Washington will have exclusive jurisdiction, and each party
consents to venue there. You may bring an eligible individual claim in small
claims court. Nothing in this section deprives you of protections or forums that
the law where you live makes mandatory.

## 12 Changes to these Terms

We may update these Terms to reflect changes in the Services, our practices, or
applicable law. The date at the top shows when the current version took effect.
If a change materially reduces your rights, we will provide reasonable advance
notice through the Services or by email when required.

Updated Terms apply prospectively when they take effect. They do not change a
locked lifetime target, completed purchase, or other term that we expressly
agreed would remain fixed. If you do not agree to an update, you must stop using
the Services, subject to any rights that survive under an earlier purchase or
mandatory law.

## 13 General terms

These Terms and the policies incorporated into them are the entire agreement
between you and the Company concerning the Services and replace earlier
agreements about the same subject, except for an offer term we expressly agreed
to preserve. If a provision is unenforceable, it will be modified only as much
as necessary, and the remaining provisions will continue in effect.

Our failure to enforce a provision is not a waiver. You may not assign these
Terms or transfer access without our written consent. We may assign these Terms
as part of a merger, acquisition, reorganization, or sale of assets, provided
your mandatory rights and any expressly preserved purchase terms remain
protected. Headings are for convenience only.

## 14 Contact

The Services are provided by {publicEnv.siteLegalEntity}. Questions, notices,
refund requests, and account concerns may be sent to
[support@makefile.fyi](mailto:support@makefile.fyi).
